import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  getCollection,
  dontLoadSizeSelector,
  filterItemsForPurl,
  purlIsValid,
} from "../helpers/productHelpers";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import ProductDetailUploadButton from "./ProductDetailUploadProductButton";
import {
  ProductOptions,
  ProductQuantity,
  SizeSelector,
  VariantOptions,
} from "../imports";
import {
  checkMultipleCanvas,
  checkUploadedFile,
  checkAllFilesUploaded,
} from "../redux/creators/threekitCreators";
import ProductDetailHead from "./ProductDetailHead";
import { device } from "../../../utils/breakPoints";
import YourUploadedPhoto from "./YourUploadedPhoto";
import OutOfStockMessage from "./OutOfStockMessage";
import BulkDiscountTable from "./BulkDiscountTable";
import { isFreeItem, isPurlItem, purlDetected } from "../helpers/promoProduct";

export default function ProductDetail({
  title,
  optionsUtility,
  addToCart,
  manualPrice = null,
}) {
  const {
    hasUploadedFile,
    hasUploadedAllFiles,
    isMultipleCanvas,
    selectedProduct,
    variantOptions,
    addToCartDisabled,
    finalObj,
    cartItems,
  } = useSelector((state) => ({
    state,
    ...state.threekit,
    ...state.fabric,
    ...state.playerState,
    isMultipleCanvas: checkMultipleCanvas(state),
    hasUploadedFile: checkUploadedFile(state),
    hasUploadedAllFiles: checkAllFilesUploaded(state),
  }));
  const dispatch = useDispatch();
  const [purlDisabled, setPurlDisabled] = useState(false);

  // If a PURL is detected make sure it's valid
  useEffect(() => {
    async function checkPurlCode() {
      const isValid = await purlIsValid(null);
      if (!isValid) {
        disableForPurl("Your code is not valid");
      }
    }

    if (purlDetected()) {
      checkPurlCode();
    } else if (isPurlItem(selectedProduct?.variants[0].sku)) {
      disableForPurl("Your code is not valid");
    }
  }, []);

  // If a PURL is detected check cart for purl items
  useEffect(() => {
    if (cartItems.length && purlDetected()) {
      const purls = filterItemsForPurl(cartItems[0]) || [];
      if (purls.length > 0) {
        disableForPurl("There is already a free item in your cart");
      }
    }
  }, [cartItems]);

  // Disable upload and add to cart due to PURL code validation
  const disableForPurl = (message) => {
    setPurlDisabled(true);
    dispatch({
      type: "fabric/setError",
      payload: {
        active: true,
        message,
      },
    });
  };

  const collectionName = getCollection();
  const isAddToCartDisabled = () => {
    return !selectedProduct.available || addToCartDisabled;
  };
  let isMadeInAmerica = false;
  const displayPrice =
    manualPrice === null ? optionsUtility.totalPriceInCurrency : manualPrice;

  switch (collectionName) {
    case "single-canvas-featured-products":
      isMadeInAmerica = true;
      break;
    case "wall-display-prints":
      isMadeInAmerica = true;
      break;
    case "collage-prints":
      isMadeInAmerica = true;
      break;
    case "triptych-prints":
      isMadeInAmerica = true;
      break;
    case "metal-prints":
      isMadeInAmerica = true;
      break;
    case "glass-prints":
      isMadeInAmerica = true;
      break;
  }

  const imageHasSize = () => {
    let { imageHeightOriginal, imageWidthOriginal } = finalObj;
    if (isMultipleCanvas) {
      const nodes = finalObj.nodes;
      const firstNode = Object.keys(nodes)[0] || "";
      imageHeightOriginal = nodes[firstNode]?.imageHeightOriginal;
      imageWidthOriginal = nodes[firstNode]?.imageWidthOriginal;
    }
    return imageHeightOriginal > 0 && imageWidthOriginal > 0;
  };

  /**
   * Only load the size selector if the product needs it
   * and there is an uploaded image
   */
  const shouldLoadSizeSelector = () => {
    return !dontLoadSizeSelector(collectionName) && imageHasSize();
  };

  const isOutOfStock = () => {
    return selectedProduct && selectedProduct.tags?.includes("out_of_stock");
  };

  return (
    <Container>
      <DesktopHeadContainer>
        <ProductDetailHead
          selectedProduct={selectedProduct}
          title={title}
          onPrice={hasUploadedFile && optionsUtility.totalPriceInCurrency}
          price={displayPrice}
        />
      </DesktopHeadContainer>

      {!hasUploadedFile ? (
        <ProductDetailContainer>
          {!purlDisabled && (
            <>
              {isOutOfStock() ? (
                <OutOfStockMessage />
              ) : (
                <ProductDetailUploadButton />
              )}
            </>
          )}
        </ProductDetailContainer>
      ) : (
        <>
          <OptionContainer>
            {!isMultipleCanvas ? (
              <YourUploadedPhoto selectedProduct={selectedProduct} />
            ) : (
              <ProductDetailUploadButton />
            )}
            {shouldLoadSizeSelector() && (
              <SizeSelector
                selectedProduct={selectedProduct}
                collectionName={collectionName}
                border={optionsUtility.border.value}
              />
            )}
            {Object.keys(variantOptions).length > 0 && (
              <VariantOptions selectedProduct={selectedProduct} />
            )}
            <ProductOptions
              optionsUtility={optionsUtility}
              selectedProduct={selectedProduct}
              collectionName={collectionName}
            />
            {!isPurlItem(selectedProduct?.variants[0].sku) && (
              <ProductQuantity />
            )}
          </OptionContainer>
          <SummaryTotalPrice>
            Total Price:
            {displayPrice && <TotalPriceSpan>${displayPrice}</TotalPriceSpan>}
          </SummaryTotalPrice>
          <AgreeMessage>
            By submitting my order, I accept that my image will be printed
            exactly as shown on this page.
          </AgreeMessage>

          {hasUploadedAllFiles && !purlDisabled && (
            <AddToCartButton
              onClick={addToCart}
              disabled={isAddToCartDisabled()}
              id="add-to-cart"
            >
              {isAddToCartDisabled() ? "Out of Stock" : "Add to Cart"}
            </AddToCartButton>
          )}
        </>
      )}
      <BulkDiscountTable />
      <AdditionalMessage>
        {isMadeInAmerica && <Message>100% Made In America</Message>}
        <Message>100% Satisfaction Guarantee</Message>
        <Link href="/pages/contact-us">Need Help?</Link>
      </AdditionalMessage>
    </Container>
  );
}

const Container = styled.div``;
const AdditionalMessage = styled.div`
  display: flex;
  text-align: left;
  gap: 16px;
  flex-direction: column;
`;
const Message = styled.p`
  color: black;
  font-size: 15px;
  margin: 0;
  font-weight: 400;
`;
const Link = styled.a`
  color: #02455d;
  font-size: 15px;
  margin: 0;
  text-decoration: underline;
`;
const AgreeMessage = styled.p`
  text-align: left;
  color: #707070;
`;
const TotalPriceSpan = styled.span`
  font-weight: 800;
  margin-left: 5px;
`;
const SummaryTotalPrice = styled.p`
  text-align: left;
  font-size: 18px;
  color: black;
`;
const AddToCartButton = styled.button`
  text-align: center;
  width: 100%;
  background: var(--color-wine);
  font-size: 18px;
  padding-top: 17px;
  padding-bottom: 17px;
  margin-bottom: 24px;
  border: none;
  color: white;
  :hover,
  :active {
    cursor: pointer;
  }
  :disabled,
  [disabled] {
    opacity: 0.7;
    cursor: no-drop !important;
  }
`;
const OptionContainer = styled.div`
  padding-bottom: 6px;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;
const DesktopHeadContainer = styled.div`
  display: block;
  padding-bottom: 16px;
`;
const ProductDetailContainer = styled.div`
  width: 100%;
  display flex;
  align-items: center;
`;
